import { Input } from '@/components/ui/input';
import { useGetChatCredentials } from '@/hooks/chat/useGetChatCredentials';
import { MessageSearchResult } from '@my-ipractice/types';
import { useEffect, useState } from 'react';
import { ChannelFilters, MessageFilters, StreamChat } from 'stream-chat';
import { useDebounce } from 'use-debounce';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useChannelActionContext } from 'stream-chat-react';
import { getEnvVariables } from '@/env/getEnvVariables';
import { getUserToken } from '@/auth/streamTokenProvider';
import { useTranslation } from 'react-i18next';

export const SearchMessages = () => {
  // grab a new chat client to override the default timeout (3s)
  const apiKey = getEnvVariables().streamKey;
  const chatClient = StreamChat.getInstance(apiKey, {
    timeout: 10000,
  });

  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewResults, setViewResults] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [messageSearchResults, setMessageSearchResults] = useState<MessageSearchResult[]>([]);

  const { jumpToMessage } = useChannelActionContext();

  const { data: chatCredentials } = useGetChatCredentials();

  const onSearchMessage = async () => {
    const channelFilters: ChannelFilters = {
      type: 'messaging',
      cid: `messaging:${chatCredentials!.channelId}`,
    };
    const messageFilters: MessageFilters = { text: { $autocomplete: searchTerm } };

    await chatClient.connectUser({ id: chatCredentials!.userId }, getUserToken);

    const search = await chatClient!.search(channelFilters, messageFilters, {
      limit: 20,
      offset: 0,
    });

    if (search.results.length > 0) {
      const mappedResults = search.results.map((r) => {
        return {
          messageId: r.message.id!,
          text: r.message.text!,
          sentBy: r.message.user!.name!,
        };
      });

      setMessageSearchResults(mappedResults);
    } else {
      setMessageSearchResults([]);
    }

    setViewResults(true);
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      onSearchMessage();
    } else {
      setViewResults(false);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="pl-0 lg:pl-6 w-full">
      <Popover open={viewResults}>
        <PopoverTrigger className="w-full lg:w-auto lg:min-w-[350px]">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('chat_page.searchChatMessageTextInputPlaceholder')}
            className="w-full bg-white dark:bg-slate-950"
            data-testid="search-message-input"
          />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="min-w-[80vw] lg:min-w-[50vw]"
          onOpenAutoFocus={(e) => {
            // keep the input trigger in focus
            e.preventDefault();
          }}
          onInteractOutside={() => {
            setSearchTerm('');
            setViewResults(false);
          }}
        >
          <div className="grid gap-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto p-2">
              {messageSearchResults.length == 0 ? (
                <p>{t('chat_page.searchChatMessageNoMessagesFound')}</p>
              ) : (
                <></>
              )}
              {messageSearchResults.map((m) => {
                return (
                  <div
                    key={m.messageId}
                    className="cursor-pointer hover:scale-[1.005] transition-transform duration-75"
                    onClick={async () => {
                      await jumpToMessage(m.messageId, 1, 2000);
                      setSearchTerm('');
                      setViewResults(false);
                    }}
                  >
                    <span className="font-bold">{m.sentBy}</span>: {m.text.slice(0, 100)}
                    {m.text.length > 100 ? '...' : ''}
                  </div>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
